import { baseApi } from './baseApi';
import {
  SalesReportDto,
  SalesReportQueryDto,
  DailySalesReportDto,
  WeeklySalesReportDto,
  MonthlySalesReportDto,
  SalesProductBreakdownDto,
  HourlySalesBreakdownDto,
  SalesReportSummaryDto,
  ReportExportDto,
  ReportExportResultDto,
  ScheduledReportDto,
  CreateScheduledReportDto,
  ReportHistoryDto,
  ReportHistoryQueryDto,
  ReportStatsDto,
  EmployeePerformanceDto,
  CashierPerformanceDto,
  WithdrawalBreakdownDto,
  CashFlowReportDto,
  DiscrepancySummaryDto,
  CashRegisterSalesDetailDto,
} from '../types/reports';

export const reportsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Main sales reports
    generateSalesReport: builder.mutation<SalesReportDto, { restaurantId: string; query: SalesReportQueryDto }>({
      query: ({ restaurantId, query }) => ({
        url: `/restaurants/${restaurantId}/reports/sales`,
        method: 'POST',
        body: query,
      }),
      invalidatesTags: [
        { type: 'Report' as const, id: 'LIST' },
      ],
    }),

    // Quick reports
    getTodaysSalesReport: builder.query<DailySalesReportDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/reports/sales/today`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Report' as const, id: `today-${restaurantId}` },
      ],
    }),

    getDailySalesReport: builder.query<DailySalesReportDto, { restaurantId: string; date: string }>({
      query: ({ restaurantId, date }) => ({
        url: `/restaurants/${restaurantId}/reports/sales/daily`,
        params: { date },
      }),
      providesTags: (result, error, { restaurantId, date }) => [
        { type: 'Report' as const, id: `daily-${restaurantId}-${date}` },
      ],
    }),

    getThisWeekSalesReport: builder.query<WeeklySalesReportDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/reports/sales/this-week`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Report' as const, id: `thisweek-${restaurantId}` },
      ],
    }),

    getWeeklySalesReport: builder.query<
      WeeklySalesReportDto,
      { restaurantId: string; weekStartDate: string; weekEndDate?: string }
    >({
      query: ({ restaurantId, weekStartDate, weekEndDate }) => ({
        url: `/restaurants/${restaurantId}/reports/sales/weekly`,
        params: { weekStartDate, ...(weekEndDate && { weekEndDate }) },
      }),
      providesTags: (result, error, { restaurantId, weekStartDate, weekEndDate }) => [
        { type: 'Report' as const, id: `weekly-${restaurantId}-${weekStartDate}-${weekEndDate || 'default'}` },
      ],
    }),

    getThisMonthSalesReport: builder.query<MonthlySalesReportDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/reports/sales/this-month`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Report' as const, id: `thismonth-${restaurantId}` },
      ],
    }),

    getMonthlySalesReport: builder.query<
      MonthlySalesReportDto,
      { restaurantId: string; year: number; month: number }
    >({
      query: ({ restaurantId, year, month }) => ({
        url: `/restaurants/${restaurantId}/reports/sales/monthly`,
        params: { year, month },
      }),
      providesTags: (result, error, { restaurantId, year, month }) => [
        { type: 'Report' as const, id: `monthly-${restaurantId}-${year}-${month}` },
      ],
    }),

    // Product and sales analysis
    getTopSellingProducts: builder.query<
      SalesProductBreakdownDto[],
      { restaurantId: string; fromDate: string; toDate: string; limit?: number }
    >({
      query: ({ restaurantId, fromDate, toDate, limit = 10 }) => ({
        url: `/restaurants/${restaurantId}/reports/products/top-selling`,
        params: { fromDate, toDate, limit },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `products-${restaurantId}` },
      ],
    }),

    getProductSalesBreakdown: builder.query<
      SalesProductBreakdownDto[],
      { restaurantId: string; fromDate: string; toDate: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/reports/products/breakdown`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `products-breakdown-${restaurantId}` },
      ],
    }),

    getHourlySalesBreakdown: builder.query<
      HourlySalesBreakdownDto[],
      { restaurantId: string; date: string }
    >({
      query: ({ restaurantId, date }) => ({
        url: `/restaurants/${restaurantId}/reports/sales/hourly`,
        params: { date },
      }),
      providesTags: (result, error, { restaurantId, date }) => [
        { type: 'Report' as const, id: `hourly-${restaurantId}-${date}` },
      ],
    }),

    getReportSummary: builder.query<
      SalesReportSummaryDto,
      { restaurantId: string; fromDate: string; toDate: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/reports/sales/summary`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `summary-${restaurantId}` },
      ],
    }),

    // Growth and analytics
    calculateGrowth: builder.query<
      { currentValue: number; previousValue: number; growthPercentage: number; growthDirection: string },
      { restaurantId: string; currentValue: number; previousValue: number }
    >({
      query: ({ restaurantId, currentValue, previousValue }) => ({
        url: `/restaurants/${restaurantId}/reports/analytics/growth`,
        params: { currentValue, previousValue },
      }),
    }),

    // Export functionality
    exportReport: builder.mutation<ReportExportResultDto, { restaurantId: string; exportRequest: ReportExportDto }>({
      query: ({ restaurantId, exportRequest }) => ({
        url: `/restaurants/${restaurantId}/reports/export`,
        method: 'POST',
        body: exportRequest,
        responseHandler: (response) => {
          // Handle file download
          if (exportRequest.format === 'PDF' || exportRequest.format === 'EXCEL' || exportRequest.format === 'CSV') {
            return response.blob();
          }
          return response.json();
        },
      }),
    }),

    // Scheduled reports
    getScheduledReports: builder.query<ScheduledReportDto[], string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/reports/scheduled`,
      providesTags: (result, error, restaurantId) => [
        { type: 'ScheduledReport' as const, id: 'LIST' },
        ...(result || []).map(({ id }) => ({ type: 'ScheduledReport' as const, id })),
      ],
    }),

    createScheduledReport: builder.mutation<
      ScheduledReportDto,
      { restaurantId: string; data: CreateScheduledReportDto }
    >({
      query: ({ restaurantId, data }) => ({
        url: `/restaurants/${restaurantId}/reports/scheduled`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: 'ScheduledReport' as const, id: 'LIST' },
      ],
    }),

    updateScheduledReport: builder.mutation<
      ScheduledReportDto,
      { restaurantId: string; reportId: string; data: CreateScheduledReportDto }
    >({
      query: ({ restaurantId, reportId, data }) => ({
        url: `/restaurants/${restaurantId}/reports/scheduled/${reportId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { reportId }) => [
        { type: 'ScheduledReport' as const, id: reportId },
        { type: 'ScheduledReport' as const, id: 'LIST' },
      ],
    }),

    deleteScheduledReport: builder.mutation<
      { success: boolean },
      { restaurantId: string; reportId: string }
    >({
      query: ({ restaurantId, reportId }) => ({
        url: `/restaurants/${restaurantId}/reports/scheduled/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { reportId }) => [
        { type: 'ScheduledReport' as const, id: reportId },
        { type: 'ScheduledReport' as const, id: 'LIST' },
      ],
    }),

    toggleScheduledReport: builder.mutation<
      ScheduledReportDto,
      { restaurantId: string; reportId: string; isActive: boolean }
    >({
      query: ({ restaurantId, reportId, isActive }) => ({
        url: `/restaurants/${restaurantId}/reports/scheduled/${reportId}/toggle`,
        method: 'POST',
        body: { isActive },
      }),
      invalidatesTags: (result, error, { reportId }) => [
        { type: 'ScheduledReport' as const, id: reportId },
        { type: 'ScheduledReport' as const, id: 'LIST' },
      ],
    }),

    // Report history
    getReportHistory: builder.query<
      ReportHistoryDto[],
      { restaurantId: string; query?: ReportHistoryQueryDto }
    >({
      query: ({ restaurantId, query = {} }) => ({
        url: `/restaurants/${restaurantId}/reports/history`,
        params: query,
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `history-${restaurantId}` },
      ],
    }),

    deleteReport: builder.mutation<{ success: boolean }, { restaurantId: string; reportId: string }>({
      query: ({ restaurantId, reportId }) => ({
        url: `/restaurants/${restaurantId}/reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Report' as const, id: 'LIST' },
      ],
    }),

    // Statistics and validation
    getReportStatistics: builder.query<
      ReportStatsDto,
      { restaurantId: string; fromDate?: string; toDate?: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/reports/statistics`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `stats-${restaurantId}` },
      ],
    }),

    checkDataAvailability: builder.query<
      { isAvailable: boolean; fromDate: string; toDate: string },
      { restaurantId: string; fromDate: string; toDate: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/reports/data-availability`,
        params: { fromDate, toDate },
      }),
    }),

    validateReportQuery: builder.mutation<
      { isValid: boolean; optimizedQuery?: SalesReportQueryDto; validationTimestamp: string },
      { restaurantId: string; query: SalesReportQueryDto }
    >({
      query: ({ restaurantId, query }) => ({
        url: `/restaurants/${restaurantId}/reports/validate-query`,
        method: 'POST',
        body: query,
      }),
    }),

    getLastDataUpdate: builder.query<
      { lastUpdate: string; currentTime: string },
      string
    >({
      query: (restaurantId) => `/restaurants/${restaurantId}/reports/last-update`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Report' as const, id: `update-${restaurantId}` },
      ],
    }),

    // Report templates and presets
    getReportTemplates: builder.query<any[], string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/reports/templates`,
      providesTags: (result, error, restaurantId) => [
        { type: 'ReportTemplate' as const, id: 'LIST' },
      ],
    }),

    getReportPresets: builder.query<any[], string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/reports/presets`,
      providesTags: (result, error, restaurantId) => [
        { type: 'ReportPreset' as const, id: 'LIST' },
      ],
    }),

    // Real-time report polling for today's data
    getTodaysSalesReportPolling: builder.query<DailySalesReportDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/reports/sales/today`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Report' as const, id: `today-polling-${restaurantId}` },
      ],
    }),

    getReportStatisticsPolling: builder.query<ReportStatsDto, string>({
      query: (restaurantId) => `/restaurants/${restaurantId}/reports/statistics`,
      providesTags: (result, error, restaurantId) => [
        { type: 'Report' as const, id: `stats-polling-${restaurantId}` },
      ],
    }),

    // Employee/Staff performance
    getEmployeePerformance: builder.query<
      EmployeePerformanceDto[],
      { restaurantId: string; fromDate: string; toDate: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/reports/employee-performance`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `employee-performance-${restaurantId}` },
      ],
    }),

    getCashierPerformance: builder.query<
      CashierPerformanceDto[],
      { restaurantId: string; fromDate: string; toDate: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/reports/cashier-performance`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `cashier-performance-${restaurantId}` },
      ],
    }),

    // Cash movement reports
    getWithdrawalBreakdown: builder.query<
      WithdrawalBreakdownDto,
      { restaurantId: string; fromDate: string; toDate: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/reports/cash-movements/withdrawal-breakdown`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `withdrawal-breakdown-${restaurantId}` },
      ],
    }),

    getCashFlowReport: builder.query<
      CashFlowReportDto,
      { restaurantId: string; fromDate: string; toDate: string; groupBy?: string }
    >({
      query: ({ restaurantId, fromDate, toDate, groupBy = 'day' }) => ({
        url: `/restaurants/${restaurantId}/reports/cash-movements/cash-flow`,
        params: { fromDate, toDate, groupBy },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `cash-flow-${restaurantId}` },
      ],
    }),

    getDiscrepancySummary: builder.query<
      DiscrepancySummaryDto,
      { restaurantId: string; fromDate: string; toDate: string }
    >({
      query: ({ restaurantId, fromDate, toDate }) => ({
        url: `/restaurants/${restaurantId}/reports/cash-movements/discrepancy-summary`,
        params: { fromDate, toDate },
      }),
      providesTags: (result, error, { restaurantId }) => [
        { type: 'Report' as const, id: `discrepancy-summary-${restaurantId}` },
      ],
    }),

    // Sales detail by cash register
    getCashRegisterSalesDetail: builder.query<
      CashRegisterSalesDetailDto,
      { restaurantId: string; cashRegisterId: string; paymentMethodFilter?: string }
    >({
      query: ({ restaurantId, cashRegisterId, paymentMethodFilter }) => ({
        url: `/restaurants/${restaurantId}/reports/cash-registers/${cashRegisterId}/sales-detail`,
        params: paymentMethodFilter ? { paymentMethodFilter } : undefined,
      }),
      providesTags: (result, error, { cashRegisterId }) => [
        { type: 'Report' as const, id: `sales-detail-${cashRegisterId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGenerateSalesReportMutation,
  useGetTodaysSalesReportQuery,
  useGetDailySalesReportQuery,
  useGetThisWeekSalesReportQuery,
  useGetWeeklySalesReportQuery,
  useGetThisMonthSalesReportQuery,
  useGetMonthlySalesReportQuery,
  useGetTopSellingProductsQuery,
  useGetProductSalesBreakdownQuery,
  useGetHourlySalesBreakdownQuery,
  useGetReportSummaryQuery,
  useCalculateGrowthQuery,
  useExportReportMutation,
  useGetScheduledReportsQuery,
  useCreateScheduledReportMutation,
  useUpdateScheduledReportMutation,
  useDeleteScheduledReportMutation,
  useToggleScheduledReportMutation,
  useGetReportHistoryQuery,
  useDeleteReportMutation,
  useGetReportStatisticsQuery,
  useCheckDataAvailabilityQuery,
  useValidateReportQueryMutation,
  useGetLastDataUpdateQuery,
  useGetReportTemplatesQuery,
  useGetReportPresetsQuery,

  // Polling hooks
  useGetTodaysSalesReportPollingQuery,
  useGetReportStatisticsPollingQuery,

  // Employee/Staff performance hooks
  useGetEmployeePerformanceQuery,
  useGetCashierPerformanceQuery,

  // Cash movement report hooks
  useGetWithdrawalBreakdownQuery,
  useGetCashFlowReportQuery,
  useGetDiscrepancySummaryQuery,
  useGetCashRegisterSalesDetailQuery,
} = reportsApi;